// hooks/useSectionAccess.ts
/**
 * What the current viewer is shown of a menu or submenu, on top of the
 * customer truth in `useMenuVisibility` (which stays bypass-free: landing,
 * docs, the assistant and the MCP read the customer view from it).
 *
 * A superadmin sees every section the account has, including the ones hidden
 * from the account's members, and those are marked `hiddenFromCustomer`
 * (plan 2026-10-23-superadmin-sees-sections-hidden-from-the-customer §1).
 * A submenu's feature flag still applies to the superadmin: a flag that is off
 * means the feature does not run for this account, so there is nothing to see.
 *
 * `hiddenFromCustomer` is about `menu_visibility` only. A role rule (Analyst is
 * OWNER/ADMIN) does not hide anything "from the customer"; consumers keep
 * applying their role rules separately.
 */

import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { MENU_REGISTRY, submenuFeatureFlag } from "@/constants/menuRegistry";
import { useMenuVisibility } from "@/hooks/useMenuVisibility";
import { useViewAsCustomer } from "@/hooks/useViewAsCustomer";

export type SectionAccess = {
  /** Render the item / admit the route for this viewer. */
  shown: boolean;
  /** Shown only because the viewer is a superadmin; members don't see it. */
  hiddenFromCustomer: boolean;
};

export function useSectionAccess() {
  const { isMenuVisible, isSubmenuVisible } = useMenuVisibility();
  const { user, activeAccount } = useAuth();
  const viewAsCustomer = useViewAsCustomer();
  const isSuperadmin = !!user?.isSuperadmin && !viewAsCustomer;
  const flags = activeAccount?.featureFlags;

  return useMemo(() => {
    function flagOn(menuKey: string, submenuKey: string): boolean {
      const flag = submenuFeatureFlag(menuKey, submenuKey);
      return !flag || flags?.[flag] === true;
    }

    function submenu(menuKey: string, submenuKey: string): SectionAccess {
      const customerVisible = isMenuVisible(menuKey) && isSubmenuVisible(menuKey, submenuKey);
      if (customerVisible) return { shown: true, hiddenFromCustomer: false };
      if (isSuperadmin && flagOn(menuKey, submenuKey)) {
        return { shown: true, hiddenFromCustomer: true };
      }
      return { shown: false, hiddenFromCustomer: false };
    }

    function menu(menuKey: string): SectionAccess {
      const subs = MENU_REGISTRY.find((s) => s.key === menuKey)?.submenus ?? [];
      // A menu that is on but has every submenu off is hidden all the same:
      // its members land on nothing there.
      const customerVisible =
        isMenuVisible(menuKey) &&
        (subs.length === 0 || subs.some((s) => isSubmenuVisible(menuKey, s.key)));
      if (customerVisible) return { shown: true, hiddenFromCustomer: false };
      if (isSuperadmin) return { shown: true, hiddenFromCustomer: true };
      // Today's rule for a member: the top-level item follows the menu key.
      return { shown: isMenuVisible(menuKey), hiddenFromCustomer: false };
    }

    return { menu, submenu, flagOn, isSuperadmin };
  }, [isMenuVisible, isSubmenuVisible, isSuperadmin, flags]);
}
